import React, { useMemo, useContext, useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import {
    ComposedChart,
    Bar,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import { ThemeContext } from 'styled-components';

const dayKey = (d) => {
    const date = new Date(d);
    return date.toISOString().slice(0, 10);
};

const LABELS = {
    statementCost: 'Statement cost',
    paid: 'Paid',
    charged: 'Billed',
    collected: 'Collected',
    billed: 'Billed others',
};

/**
 * Time-based chart:
 * - Bars: total statement cost per day (all statements).
 * - Line "Paid": you paid out (transactions where you're the payer).
 * - Line "Charged": you were charged (statement costs on bills you don't own).
 * - Line "Collected": you were paid (transactions on your statements where someone else paid).
 * - Line "Billed": you charged (statement costs on bills you own).
 */
const DIM_OPACITY = 0.25;

const DashboardActivityChart = ({
    statements = [],
    transactions = [],
    currentUserSub,
    ownedStatementIds = [],
}) => {
    const theme = useContext(ThemeContext);
    const [highlightedKey, setHighlightedKey] = useState(null);
    const textColor = theme?.colors?.text || '#333';
    const barColor = theme?.colors?.disabled || 'rgba(0, 0, 0, 0.26)';
    const colors = {
        paid: theme?.colors?.brandSecondary || '#1976d2',
        charged: theme?.colors?.error || '#f44336',
        collected: theme?.colors?.success || '#4caf50',
        billed: theme?.colors?.warning || '#ff9800',
    };

    const ownedSet = useMemo(
        () => new Set((ownedStatementIds || []).map(String)),
        [ownedStatementIds]
    );

    const chartData = useMemo(() => {
        const byDay = {};
        const ensure = (key) => {
            if (!byDay[key])
                byDay[key] = {
                    date: key,
                    statementCost: 0,
                    paid: 0,
                    charged: 0,
                    collected: 0,
                    billed: 0,
                };
            return byDay[key];
        };

        const mySub = currentUserSub ? String(currentUserSub).toUpperCase() : '';

        // Bars: total statement cost. Lines: billed (your statements) vs charged (others' statements).
        statements.forEach((s) => {
            const key = dayKey(s.created_at);
            const row = ensure(key);
            const cost = Number(s.cost) || 0;
            row.statementCost += cost;
            if (ownedSet.has(String(s.id))) row.billed += cost;
            else row.charged += cost;
        });

        // Paid: you paid out. Collected: paid to you (your statement, other payer).
        transactions.forEach((t) => {
            const key = dayKey(t.created_at);
            const row = ensure(key);
            const amount = Math.abs(Number(t.amount)) || 0;
            const isMe = t.user?.sub && String(t.user.sub).toUpperCase() === mySub;
            const stmtId = t.statement_id ?? t.statementId;
            const isMyStatement = stmtId != null && ownedSet.has(String(stmtId));

            if (isMe) row.paid += amount;
            else if (isMyStatement) row.collected += amount;
        });

        return Object.values(byDay).sort((a, b) => a.date.localeCompare(b.date));
    }, [statements, transactions, currentUserSub, ownedSet]);

    const [animationProgress, setAnimationProgress] = useState(0);
    const ANIMATION_MS = 500;

    useEffect(() => {
        setAnimationProgress(0);
        const start = performance.now();
        const tick = (now) => {
            const elapsed = now - start;
            const next = Math.min(elapsed / ANIMATION_MS, 1);
            setAnimationProgress(next);
            if (next < 1) requestAnimationFrame(tick);
        };
        const id = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(id);
    }, [chartData]);

    const displayData = useMemo(() => {
        if (animationProgress >= 1) return chartData;
        return chartData.map((row) => ({
            ...row,
            statementCost: row.statementCost * animationProgress,
            paid: row.paid * animationProgress,
            charged: row.charged * animationProgress,
            collected: row.collected * animationProgress,
            billed: row.billed * animationProgress,
        }));
    }, [chartData, animationProgress]);

    // Cap so one bad data point (e.g. 8899999999998) doesn't blow up the y-axis
    const Y_DOMAIN_SANITY_CAP = 1e9;

    const yDomainMax = useMemo(() => {
        let max = 0;
        chartData.forEach((row) => {
            const values = [
                row.statementCost,
                row.paid,
                row.charged,
                row.collected,
                row.billed,
            ].filter((v) => typeof v === 'number' && v <= Y_DOMAIN_SANITY_CAP);
            if (values.length) max = Math.max(max, ...values);
        });
        if (max <= 0) return 1;
        return Number(Number(max).toFixed(2));
    }, [chartData]);

    if (chartData.length === 0) {
        return (
            <Box sx={{ py: 3, px: 2, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: textColor, fontStyle: 'italic' }}>
                    No activity to show yet. Add statements or payments to see the chart.
                </Typography>
            </Box>
        );
    }

    const formatTick = (value) =>
        value >= 1000 ? `$${(value / 1000).toFixed(1)}k` : `$${Math.round(value)}`;
    const formatTooltip = (value, name) => {
        const label = LABELS[name] || name;
        return [typeof value === 'number' ? `$${Number(value).toFixed(2)}` : value, label];
    };

    const isHighlighted = (key) => highlightedKey == null || highlightedKey === key;

    const renderLegend = (props) => {
        const { payload } = props;
        return (
            <Box
                component="ul"
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    justifyContent: 'center',
                    listStyle: 'none',
                    margin: 0,
                    padding: '8px 0 0',
                }}
            >
                {payload.map((entry) => {
                    const key = entry.dataKey;
                    const active = isHighlighted(key);
                    return (
                        <Box
                            component="li"
                            key={key}
                            onMouseEnter={() => setHighlightedKey(key)}
                            onMouseLeave={() => setHighlightedKey(null)}
                            onClick={() => setHighlightedKey(active && highlightedKey !== null ? null : key)}
                            sx={{
                                cursor: 'pointer',
                                opacity: active ? 1 : DIM_OPACITY,
                                fontWeight: active ? 600 : 400,
                                transition: 'opacity 0.15s ease, font-weight 0.15s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                color: textColor,
                                '&:hover': { opacity: 1 },
                            }}
                        >
                            <Box
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: 1,
                                    backgroundColor: key === 'statementCost' ? barColor : colors[key],
                                }}
                            />
                            <span>{LABELS[key] || key}</span>
                        </Box>
                    );
                })}
            </Box>
        );
    };

    return (
        <Box sx={{ width: '100%', height: 320, px: { xs: 0, sm: 1 }, py: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                    data={displayData}
                    margin={{ top: 12, right: 12, left: 8, bottom: 8 }}
                >
                    <CartesianGrid strokeDasharray="3 3" stroke={theme?.colors?.border || 'rgba(0,0,0,0.08)'} />
                    <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11, fill: textColor }}
                        tickFormatter={(v) => {
                            const d = new Date(v);
                            return `${d.getMonth() + 1}/${d.getDate()}`;
                        }}
                        stroke={textColor}
                    />
                    <YAxis
                        domain={[0, yDomainMax]}
                        tick={{ fontSize: 11, fill: textColor }}
                        tickFormatter={formatTick}
                        stroke={textColor}
                    />
                    <Tooltip
                        contentStyle={{
                            backgroundColor: theme?.colors?.backgroundPrimary || '#fff',
                            border: `1px solid ${theme?.colors?.border || '#eee'}`,
                            borderRadius: 8,
                        }}
                        labelStyle={{ color: textColor }}
                        formatter={formatTooltip}
                        labelFormatter={(label) => new Date(label).toLocaleDateString()}
                    />
                    <Legend content={renderLegend} />
                    <Bar
                        dataKey="statementCost"
                        name="statementCost"
                        fill={barColor}
                        fillOpacity={isHighlighted('statementCost') ? 1 : DIM_OPACITY}
                        radius={[4, 4, 0, 0]}
                        barSize={20}
                        isAnimationActive={false}
                    />
                    <Line
                        type="monotone"
                        dataKey="paid"
                        name="paid"
                        stroke={colors.paid}
                        strokeOpacity={isHighlighted('paid') ? 1 : DIM_OPACITY}
                        strokeWidth={isHighlighted('paid') ? 2.5 : 2}
                        dot={{ r: 3, fill: colors.paid, fillOpacity: isHighlighted('paid') ? 1 : DIM_OPACITY }}
                        isAnimationActive={false}
                    />
                    <Line
                        type="monotone"
                        dataKey="collected"
                        name="collected"
                        stroke={colors.collected}
                        strokeOpacity={isHighlighted('collected') ? 1 : DIM_OPACITY}
                        strokeWidth={isHighlighted('collected') ? 2.5 : 2}
                        dot={{ r: 3, fill: colors.collected, fillOpacity: isHighlighted('collected') ? 1 : DIM_OPACITY }}
                        isAnimationActive={false}
                    />
                    <Line
                        type="monotone"
                        dataKey="charged"
                        name="charged"
                        stroke={colors.charged}
                        strokeOpacity={isHighlighted('charged') ? 1 : DIM_OPACITY}
                        strokeWidth={isHighlighted('charged') ? 2.5 : 2}
                        dot={{ r: 3, fill: colors.charged, fillOpacity: isHighlighted('charged') ? 1 : DIM_OPACITY }}
                        isAnimationActive={false}
                    />
                    <Line
                        type="monotone"
                        dataKey="billed"
                        name="billed"
                        stroke={colors.billed}
                        strokeOpacity={isHighlighted('billed') ? 1 : DIM_OPACITY}
                        strokeWidth={isHighlighted('billed') ? 2.5 : 2}
                        dot={{ r: 3, fill: colors.billed, fillOpacity: isHighlighted('billed') ? 1 : DIM_OPACITY }}
                        isAnimationActive={false}
                    />
                </ComposedChart>
            </ResponsiveContainer>
        </Box>
    );
};

export default DashboardActivityChart;
